import { style } from "@vanilla-extract/css";
import { borderRadius } from "@/client/ui/design-system/styles/border-radius.css";
import { colors } from "@/client/ui/design-system/styles/colors.css";
import { boxShadow } from "@/client/ui/design-system/styles/shadows.css";
import { spacing } from "@/client/ui/design-system/styles/spacing.css";
import { body3Css } from "@/client/ui/design-system/styles/typography.css";
import { kCompactSelector } from "@/client/utils/displayMode";
import { px } from "@/client/utils/units";

// The design system has no yellow scale, so this stays local until it gets
// one. The tag is an outline, so it reads as a stamp rather than a button:
// light yellow carries the border and the text, toned down so it sits behind
// the title it marks. Every place it appears is dark.
const kTagBorder = "rgba(255, 243, 184, 0.4)";
const kTagText = "rgba(255, 243, 184, 0.75)";

// A pixel of horizontal padding gives way to the border on each side.
export const tag = style({
    ...body3Css,
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    padding: `${px(3)} ${px(7)}`,
    border: `${px(1)} solid ${kTagBorder}`,
    borderRadius: borderRadius.pill,
    backgroundColor: "transparent",
    color: kTagText,
    fontSize: px(11),
    fontWeight: 500,
    lineHeight: "1",
    whiteSpace: "nowrap",
    cursor: "inherit",
});

// The home-card size: "R&D" in a smaller stamp.
export const tagCompact = style({
    padding: `${px(3)} ${px(6)}`,
    fontSize: px(10),
});

export const tooltip = style({
    ...body3Css,
    position: "absolute",
    zIndex: 1000,
    width: "max-content",
    maxWidth: px(240),
    padding: spacing.xs,
    borderRadius: borderRadius.md,
    backgroundColor: colors.gray90,
    boxShadow: `${boxShadow.ring}, ${boxShadow.md}`,
    color: colors.gray10,
    fontSize: px(12),
    fontWeight: 400,
    lineHeight: "1.4",
    whiteSpace: "normal",
    textAlign: "left",
    opacity: 0,
    visibility: "hidden",
    pointerEvents: "none",
    transition: "opacity 0.12s ease",
    selectors: {
        // `:focus` rather than `:focus-visible`, so a tap on the tag reveals it
        // too. The anchor line is for the home card, where the tag is not
        // focusable and the card link is the focus stop.
        [`${tag}:hover > &, ${tag}:focus > &, a:focus-visible ${tag} > &`]: {
            opacity: 1,
            visibility: "visible",
        },
        // A phone or plugin panel is narrower than the bubble.
        [kCompactSelector]: {
            maxWidth: "min(240px, 70vw)",
        },
    },
});

// Which edge of the tag the bubble lines up with. `end` grows leftward, which
// keeps it inside a card at the right of the page; `start` grows rightward,
// for a tag at the left of a container that clips (a header, a content pane).
export const tooltipEnd = style({ right: 0 });

export const tooltipStart = style({ left: 0 });

export const tooltipAbove = style({
    bottom: `calc(100% + ${px(6)})`,
});

export const tooltipBelow = style({
    top: `calc(100% + ${px(6)})`,
});
